import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import ICONS from "../../../../assets/Icons";
import CommonToast from "../../../../components/_utils/CommonToast";
import { env } from "../../../../env";
import axiosInstance from "../../../globalInspector/axiosInstance";

const baseUrl = env.REACT_APP_BASE_URL;

const getHeaders = () => ({
  "Content-Type": "application/json",
  "X-Api-Key": sessionStorage.getItem("q2p_token"),
});

export const scheduledInternalCreate = createAsyncThunk(
  "scheduleInternalTransactions/scheduledInternalCreate",
  async (values, { rejectWithValue }) => {
    const { scheduledInternalTxnData } = values;

    try {
      const response = await axiosInstance.post(
        `${baseUrl}/transactions/scheduled_internal`,
        scheduledInternalTxnData,
        {
          headers: getHeaders(),
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error);
    }
  },
);

export const scheduledInternalViewAll = createAsyncThunk(
  "scheduleInternalTransactions/scheduledInternalViewAll",
  async (values) => {
    const {
      page_no,
      page_item_count,
      amount,
      lifecycle_status,
      frequency,
      occurrences,
      slot,
      transaction_day,
      transaction_month,
      transaction_start_date,
      transaction_end_date,
      internal_transaction_ids,
      payment_method,
      category,
      internal_account_id,
      owner_account_id,
      confidential_transaction,
      start_date,
      end_date,
      category_value,
      category_name,
      search_string,
      distribution_lifecycle_status,
      scheduled_name,
      labels,
    } = values;

    try {
      const response = await axiosInstance.get(
        `${baseUrl}/transactions/scheduled_internal`,
        {
          headers: getHeaders(),
          params: {
            page_no,
            page_size: page_item_count,
            amount,
            lifecycle_status,
            frequency,
            occurrences,
            slot,
            transaction_day,
            transaction_month,
            transaction_start_date,
            transaction_end_date,
            internal_transaction_ids,
            payment_method,
            category,
            internal_account_id,
            owner_account_id,
            confidential_transaction:
              confidential_transaction === "Show only confidential"
                ? "Y"
                : confidential_transaction === "Hide confidential"
                  ? "N"
                  : undefined,
            start_date,
            end_date,
            search_string,
            category_name,
            category_value,
            distribution_lifecycle_status,
            scheduled_name,
            labels,
          },
        },
      );

      return response.data;
    } catch (error) {
      CommonToast({
        text: error?.response?.data?.error,
        icon: ICONS.ERROR_WITH_WHITE_CIRCLE,
        variant: "var(--error)",
        buttonNeeded: false,
      });

      throw error;
    }
  },
);

export const scheduledInternalDetailsById = createAsyncThunk(
  "scheduleInternalTransactions/scheduledInternalDetailsById",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `${baseUrl}/transactions/scheduled_internal/${id}`,
        {
          headers: getHeaders(),
        },
      );

      return response.data;
    } catch (error) {
      return rejectWithValue({
        status: error?.response?.status,
        data: error?.response?.data,
        message: error?.message,
      });
    }
  },
);
export const scheduledInternalApprove = createAsyncThunk(
  "scheduleInternalTransactions/scheduledInternalApprove",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${baseUrl}/transactions/scheduled_internal/approve`,
        {
          txn_id: Array.isArray(id) ? id : [id],
        },
        {
          headers: getHeaders(),
        },
      );

      if (!response.data?.status) {
        return rejectWithValue(response.data);
      }

      return response.data;
    } catch (error) {
      return rejectWithValue(error?.response?.data || error?.message);
    }
  },
);

export const scheduledInternalReject = createAsyncThunk(
  "scheduleInternalTransactions/scheduledInternalReject",
  async ({ id, reason }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${baseUrl}/transactions/scheduled_internal/reject`,
        {
          txn_id: Array.isArray(id) ? id : [id],
          reason,
        },
        {
          headers: getHeaders(),
        },
      );

      return response.data?.status
        ? response.data
        : rejectWithValue(response.data);
    } catch (error) {
      return rejectWithValue(error?.response?.data || error?.message);
    }
  },
);

export const scheduledInternalDiscard = createAsyncThunk(
  "scheduleInternalTransactions/scheduledInternalDiscard",
  async ({ id }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(
        `${baseUrl}/transactions/scheduled_internal/delete`,
        {
          headers: getHeaders(),
          data: {
            txn_id: [id],
          },
        },
      );
      if (!response.data?.status) {
        return rejectWithValue(response.data);
      }

      return response.data;
    } catch (error) {
      const discardError = error?.response?.data?.errors?.base?.[0]?.errors;
      const message = discardError?.join(", ") || error?.message;

      CommonToast({
        text: message,
        icon: ICONS.ERROR_WITH_WHITE_CIRCLE,
        variant: "var(--error)",
        buttonNeeded: false,
      });
      return rejectWithValue(error?.response?.data || error?.message);
    }
  },
);
export const scheduledInternalOccurrences = createAsyncThunk(
  "scheduleInternalTransactions/scheduledInternalOccurrences",
  async (paramsData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(
        `${baseUrl}/transactions/scheduled_internal/calculate_occurrences`,
        {
          headers: getHeaders(),
          params: paramsData,
        },
      );

      return response.data;
    } catch (error) {
      if (error?.response) {
        const responseData = error.response.data;

        const errorMessage =
          responseData?.errors?.category?.[0] ||
          responseData?.errors?.additional_data?.[0] ||
          responseData?.errors?.base?.[0] ||
          responseData?.errors;

        CommonToast({
          text: errorMessage,
          icon: ICONS.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
          buttonNeeded: false,
        });

        return rejectWithValue(responseData);
      }

      return rejectWithValue(error);
    }
  },
);

export const scheduledInternalAddLabel = createAsyncThunk(
  "scheduleInternalTransactions/scheduledInternalAddLabel",
  async ({ record_ids, labels }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${baseUrl}/transactions/scheduled_internal/add_label`,
        {
          record_ids,
          labels,
        },
        {
          headers: getHeaders(),
        },
      );

      return response.data?.status
        ? response.data
        : rejectWithValue(response.data);
    } catch (error) {
      return rejectWithValue(error?.response?.data || error?.message);
    }
  },
);

export const scheduledInternalRemoveLabel = createAsyncThunk(
  "scheduleInternalTransactions/scheduledInternalRemoveLabel",
  async ({ record_ids, labels }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `${baseUrl}/transactions/scheduled_internal/remove_label`,
        {
          record_ids,
          labels,
        },
        {
          headers: getHeaders(),
        },
      );

      return response.data?.status
        ? response.data
        : rejectWithValue(response.data);
    } catch (error) {
      return rejectWithValue(error?.response?.data || error?.message);
    }
  },
);

const scheduledInternalTransactionsSlice = createSlice({
  name: "scheduleInternalTransactions",
  initialState: {
    scheduledInternalCreateData: null,
    scheduledInternalCreateLoading: null,
    scheduledInternalCreateError: null,

    scheduledInternalViewData: null,
    scheduledInternalViewLoading: null,
    scheduledInternalViewError: null,

    scheduledInternalDetailsData: null,
    scheduledInternalDetailsLoading: null,
    scheduledInternalDetailsError: null,

    scheduledInternalApproveData: null,
    scheduledInternalApproveLoading: null,
    scheduledInternalApproveError: null,

    scheduledInternalRejectData: null,
    scheduledInternalRejectLoading: null,
    scheduledInternalRejectError: null,

    scheduledInternalDiscardData: null,
    scheduledInternalDiscardLoading: null,
    scheduledInternalDiscardError: null,

    scheduledInternalOccurrencesData: null,
    scheduledInternalOccurrencesLoading: null,
    scheduledInternalOccurrencesError: null,

    scheduledInternalAddLabelData: null,
    scheduledInternalAddLabelLoading: false,
    scheduledInternalAddLabelError: null,

    scheduledInternalRemoveLabelData: null,
    scheduledInternalRemoveLabelLoading: false,
    scheduledInternalRemoveLabelError: null,

    scheduledInternalAvailableLabels: [],

    storedPage: 1,
  },
  reducers: {
    resetStoredPage: (state) => {
      state.storedPage = 1;
    },
    setStoredPage: (state, action) => {
      state.storedPage = action.payload;
    },
    clearScheduledInternalErrors: (state) => {
      state.scheduledInternalApproveError = null;
      state.scheduledInternalRejectError = null;
    },
    setScheduledInternalDetails: (state, action) => {
      state.scheduledInternalDetailsData = action.payload;
    },
    resetScheduledInternalAddLabel: (state) => {
      state.scheduledInternalAddLabelData = null;
      state.scheduledInternalAddLabelLoading = false;
      state.scheduledInternalAddLabelError = null;
    },
    resetScheduledInternalRemoveLabel: (state) => {
      state.scheduledInternalRemoveLabelData = null;
      state.scheduledInternalRemoveLabelLoading = false;
      state.scheduledInternalRemoveLabelError = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(scheduledInternalCreate.pending, (state) => {
        state.scheduledInternalCreateLoading = true;
      })
      .addCase(scheduledInternalCreate.fulfilled, (state, action) => {
        state.scheduledInternalCreateLoading = false;
        state.scheduledInternalCreateData = action?.payload;
      })
      .addCase(scheduledInternalCreate.rejected, (state, action) => {
        state.scheduledInternalCreateLoading = false;
        state.scheduledInternalCreateData = false;
        state.scheduledInternalCreateError = action?.payload;
      })
      .addCase(scheduledInternalViewAll.pending, (state) => {
        state.scheduledInternalViewLoading = true;
      })
      .addCase(scheduledInternalViewAll.fulfilled, (state, action) => {
        state.scheduledInternalViewLoading = false;
        const isPendingRequest =
          action?.meta?.arg?.lifecycle_status === "pending";
        // if (!isPendingRequest) {
          state.scheduledInternalViewData = action?.payload;
          const availableLabels =
            action?.payload?.summary?.available_labels || [];
          state.scheduledInternalAvailableLabels = availableLabels;
          sessionStorage.setItem(
            "scheduleInternalTxnLabels",
            JSON.stringify(availableLabels),
          );
        // }
      })
      .addCase(scheduledInternalViewAll.rejected, (state, action) => {
        state.scheduledInternalViewLoading = false;
        state.scheduledInternalViewData = false;
        state.scheduledInternalViewError = action?.payload;
      })
      .addCase(scheduledInternalDetailsById.pending, (state) => {
        state.scheduledInternalDetailsLoading = true;
      })
      .addCase(scheduledInternalDetailsById.fulfilled, (state, action) => {
        state.scheduledInternalDetailsLoading = false;
        state.scheduledInternalDetailsData = action?.payload;
      })
      .addCase(scheduledInternalDetailsById.rejected, (state, action) => {
        state.scheduledInternalDetailsData = false;
        state.scheduledInternalDetailsLoading = false;
        state.scheduledInternalDetailsError = action?.payload;
      })
      .addCase(scheduledInternalApprove.pending, (state) => {
        state.scheduledInternalApproveLoading = true;
      })
      .addCase(scheduledInternalApprove.fulfilled, (state, action) => {
        state.scheduledInternalApproveLoading = false;
        state.scheduledInternalApproveData = action?.payload;
      })
      .addCase(scheduledInternalApprove.rejected, (state, action) => {
        sessionStorage.setItem(
          "ScheduleInternalMultiDataError",
          JSON.stringify(action.payload?.errors?.base),
        );
        state.scheduledInternalApproveData = false;
        state.scheduledInternalApproveLoading = false;
        state.scheduledInternalApproveError = action?.payload;
      })
      .addCase(scheduledInternalReject.pending, (state) => {
        state.scheduledInternalRejectLoading = true;
      })
      .addCase(scheduledInternalReject.fulfilled, (state, action) => {
        state.scheduledInternalRejectLoading = false;
        state.scheduledInternalRejectData = action?.payload;
      })
      .addCase(scheduledInternalReject.rejected, (state, action) => {
        sessionStorage.setItem(
          "ScheduleInternalMultiDataError",
          JSON.stringify(action.payload?.errors?.base),
        );
        state.scheduledInternalRejectLoading = false;
        state.schedulesInternalTxnRejectError = action?.payload;
        state.scheduledInternalRejectData = false;
      })
      .addCase(scheduledInternalDiscard.pending, (state) => {
        state.scheduledInternalDiscardLoading = true;
      })
      .addCase(scheduledInternalDiscard.fulfilled, (state, action) => {
        state.scheduledInternalDiscardLoading = false;
        state.scheduledInternalDiscardData = action?.payload;
      })
      .addCase(scheduledInternalDiscard.rejected, (state, action) => {
        state.scheduledInternalDiscardLoading = false;
        state.scheduledInternalDiscardError = action?.payload;
        state.scheduledInternalDiscardData = false;
      })
      .addCase(scheduledInternalOccurrences.pending, (state) => {
        state.scheduledInternalOccurrencesLoading = true;
      })
      .addCase(scheduledInternalOccurrences.fulfilled, (state, action) => {
        state.scheduledInternalOccurrencesLoading = false;
        state.scheduledInternalOccurrencesData = action?.payload;
      })
      .addCase(scheduledInternalOccurrences.rejected, (state, action) => {
        state.scheduledInternalOccurrencesLoading = false;
        state.scheduledInternalOccurrencesData = false;
        state.scheduledInternalOccurrencesError = action?.payload;
      })
     .addCase(scheduledInternalAddLabel.pending, (state) => {
        state.scheduledInternalAddLabelLoading = true;
        state.scheduledInternalAddLabelError = null;
      })
      .addCase(scheduledInternalAddLabel.fulfilled, (state, action) => {
        state.scheduledInternalAddLabelLoading = false;
        state.scheduledInternalAddLabelData = action.payload;
      })
      .addCase(scheduledInternalAddLabel.rejected, (state, action) => {
        state.scheduledInternalAddLabelLoading = false;
        state.scheduledInternalAddLabelData = null;
        state.scheduledInternalAddLabelError = action.payload || true;
      })

      .addCase(scheduledInternalRemoveLabel.pending, (state) => {
        state.scheduledInternalRemoveLabelLoading = true;
        state.scheduledInternalRemoveLabelError = null;
      })
      .addCase(scheduledInternalRemoveLabel.fulfilled, (state, action) => {
        state.scheduledInternalRemoveLabelLoading = false;
        state.scheduledInternalRemoveLabelData = action.payload;
      })
      .addCase(scheduledInternalRemoveLabel.rejected, (state, action) => {
        state.scheduledInternalRemoveLabelLoading = false;
        state.scheduledInternalRemoveLabelData = null;
        state.scheduledInternalRemoveLabelError = action.payload || true;
      });
  },
});

export const {
  resetStoredPage,
  setStoredPage,
  clearScheduledInternalErrors,
  setScheduledInternalDetails,
  resetScheduledInternalAddLabel,
  resetScheduledInternalRemoveLabel,
} = scheduledInternalTransactionsSlice.actions;
export default scheduledInternalTransactionsSlice.reducer;
